import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Card from '@mui/material/Card'
import Slide from '@mui/material/Slide'
import { useData } from './hooks'
import DrawerContentsSingle from './DrawerContentsSingle/DrawerContentsSingle'
import { PestPacDetailContent } from './DrawerContentsSingle/PestPacDetailContent'
import EntityDetailsDrawerSkeleton from './EntityDetailsDrawerSkeleton'
import Box from '@mui/material/Box/Box'
import IconButton from '@mui/material/IconButton'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { EntityLocationDTO } from '@user/store/map/typings'
import { useSelector } from 'react-redux'
import {
  selectPestPacLocationDetail,
  selectPestPacLocationDetailStatus,
  setPestPacLocationDetail,
} from '@user/store/map'
import { useGetLocationUrl } from '@user/store/authorization/hooks/useGetLocationUrl'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { updateKnockStatusHistoryById } from '@user/store/knockStatus/api'
import { toast } from '@sales-center/components'

type Props = {
  show: boolean
  multiTenantDrawerOpen: boolean
  onClose: () => void
  onBackClick?: () => void
  drawerVariant?: 'full' | 'modal'
  refreshTrigger?: number
  onKnockStatusClick: (location: EntityLocationDTO) => void
  onViewKnockStatusHistory?: (location: EntityLocationDTO) => void
  onRefresh?: () => void
  onRefreshPins?: () => void
  onReviewPendingLead?: (draftId: string) => void
  onPestPacAddLead?: (location: EntityLocationDTO) => void
}

function EntityDetailsDrawer(props: Props): JSX.Element {
  const {
    onClose,
    onBackClick,
    show,
    drawerVariant = 'full',
    multiTenantDrawerOpen,
    refreshTrigger,
    onKnockStatusClick,
    onViewKnockStatusHistory,
    onRefresh,
    onRefreshPins,
    onReviewPendingLead,
    onPestPacAddLead,
  } = props

  const {
    entityDetails,
    entityStatus,
    serviceLocation,
    variant,
    pestPacPin,
    refetchEntityDetails,
    refetchPestPacDetail,
    handleViewKnockStatusHistory,
    handleDrawerKnockStatusClick,
  } = useData({
    onKnockStatusClick,
    onViewKnockStatusHistory,
  })

  const pestPacDetail = useSelector(selectPestPacLocationDetail)
  const pestPacDetailStatus = useSelector(selectPestPacLocationDetailStatus)
  const isPestPacDetail = pestPacDetailStatus !== 'empty'
  const pestPacLocationUrl = useGetLocationUrl(
    pestPacDetail?.locationId ?? '',
    false,
  )

  const dispatch = useAppDispatch()
  const [isSavingPestPacReason, setIsSavingPestPacReason] = useState(false)

  // Backend EntityType is a strict enum (Lead/Opportunity/DraftLead/
  // KnockStatus). A PestPac location's knock status is never tied to a real
  // Lead/Opp/DraftLead record, so this must always be 'KnockStatus' - same
  // reasoning as the preview bubble's equivalent save handler.
  const handleSavePestPacReason = useCallback(
    async (newReason: string | null) => {
      if (!pestPacDetail?.knockStatus?.id) return false
      setIsSavingPestPacReason(true)
      try {
        const entityType = 'KnockStatus'
        await dispatch(
          updateKnockStatusHistoryById({
            id: pestPacDetail.knockStatus.id,
            entityType,
            input: {
              customReason: newReason,
              knockStatusName: null,
              previousKnockStatusName: null,
              entityType,
              entityId: 0,
            },
          }),
        ).unwrap()
        dispatch(
          setPestPacLocationDetail({
            ...pestPacDetail,
            knockStatus: {
              ...pestPacDetail.knockStatus,
              reason: newReason,
            },
          }),
        )
        toast.success('Reason updated')
        return true
      } catch (e: any) {
        toast.error(e?.message || 'Failed to update reason')
        return false
      } finally {
        setIsSavingPestPacReason(false)
      }
    },
    [pestPacDetail, dispatch],
  )

  const isInitialMount = useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (refreshTrigger !== undefined && show) {
      refetchEntityDetails(true)
      refetchPestPacDetail()
    }
  }, [refreshTrigger])

  const isMobile = useMediaQuery(`(max-width: 600px)`)

  const isFullVariant = React.useMemo(() => {
    return multiTenantDrawerOpen && isMobile ? true : drawerVariant === 'full'
  }, [drawerVariant, isMobile, multiTenantDrawerOpen, show])

  const { left, top } = useMemo(() => {
    const toolbarElement = document.getElementById('mapViewToolbarContainer')
    const toolbarWidth = toolbarElement ? toolbarElement.clientWidth : 0

    if (isMobile) {
      return { left: 0, top: '64px' }
    }

    return isFullVariant
      ? { left: toolbarWidth, top: 0 }
      : { left: (multiTenantDrawerOpen ? 407 : 0) + toolbarWidth, top: 40 }
  }, [isFullVariant, isMobile, show])

  const numericTop = typeof top === 'string' ? parseInt(top, 10) : top
  const modalHeight = `calc(100vh - ${numericTop}px)`

  return (
    <Slide direction="right" in={show} mountOnEnter unmountOnExit>
      <Stack>
        <Box
          sx={{
            width: isMobile ? '100vw' : 400,
            position: 'fixed',
            top,
            left,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            paddingRight: 2,
            pt: 1,
            justifyContent: onBackClick ? 'space-between' : 'flex-end',
            backgroundColor: 'white',
          }}
        >
          {onBackClick ? (
            <IconButton
              aria-label="back"
              onClick={onBackClick}
              sx={{
                flex: 0,
                width: 36,
                height: 36,
                color: '#1565C0',
              }}
            >
              <ArrowBack />
            </IconButton>
          ) : null}

          <Button
            variant="text"
            aria-label="close"
            onClick={onClose}
            sx={{ color: '#1565C0' }}
          >
            Close
          </Button>
        </Box>

        <Card
          sx={{
            height: isFullVariant ? '100%' : modalHeight,
            maxHeight: isFullVariant ? '100%' : 'fit-content',
            width: isMobile ? '100vw' : 400,
            position: 'fixed',
            top,
            left,
            zIndex: 1300,
            boxShadow: 4,
            borderRadius: isFullVariant ? undefined : 3,
            overflowY: 'auto',
            paddingTop: '40px',
            paddingBottom: isMobile ? 10 : 2,
            ...(isPestPacDetail
              ? { display: 'flex', flexDirection: 'column' }
              : {}),
          }}
        >
          {isPestPacDetail ? (
            <>
              <Box sx={{ marginLeft: 2, marginRight: 2, marginTop: 0 }}>
                <Typography variant="caption" mb={'12.5px'}>
                  PestPac Account
                </Typography>
              </Box>
              <PestPacDetailContent
                detail={pestPacDetail}
                status={pestPacDetailStatus as any}
                onAddLead={() => {
                  if (pestPacPin) onPestPacAddLead?.(pestPacPin)
                }}
                onKnockStatusClick={handleDrawerKnockStatusClick}
                onViewKnockStatusHistory={handleViewKnockStatusHistory}
                onRefresh={onRefresh}
                onOpenPestPacLocation={
                  pestPacLocationUrl
                    ? () => {
                        window.open(
                          pestPacLocationUrl,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                    : undefined
                }
                isSavingReason={isSavingPestPacReason}
                onSaveReason={handleSavePestPacReason}
              />
            </>
          ) : entityDetails && entityStatus && serviceLocation && variant ? (
            <DrawerContentsSingle
              entityDetails={entityDetails}
              entityStatus={entityStatus}
              serviceLocation={serviceLocation}
              variant={variant}
              refetchEntityDetails={refetchEntityDetails}
              onKnockStatusClick={handleDrawerKnockStatusClick}
              onViewKnockStatusHistory={handleViewKnockStatusHistory}
              onBackClick={onBackClick}
              onCloseDrawer={onClose}
              onRefresh={onRefresh}
              onRefreshPins={onRefreshPins}
              onReviewPendingLead={onReviewPendingLead}
            />
          ) : (
            <>
              {entityStatus === 'error' ? (
                <Alert severity="warning">
                  You don&apos;t have permission to view this record.
                </Alert>
              ) : (
                <EntityDetailsDrawerSkeleton />
              )}
            </>
          )}
        </Card>
      </Stack>
    </Slide>
  )
}

export default EntityDetailsDrawer
